import React from 'react';
import { getFileTypeInfo } from '../../types/dataRoom';

interface FileTypeIconProps {
  fileType: string;
  /** Pass the original filename so types stored as 'unknown' (legacy rows)
   *  fall back to extension parsing. */
  fallbackFilename?: string | null;
  size?: number;
}

/**
 * Document-with-folded-corner pictogram with a coloured label band.
 * Same shape and dimensions for every file type — long extensions
 * (PowerPoint, Spreadsheet) cannot overflow the way the old
 * fixed-width text badges did because the label is hard-capped at 3
 * characters by `getFileTypeInfo.short`.
 *
 * Rendered as a single inline SVG so it stays sharp at any DPR and
 * inherits no layout from CSS classes — drop it anywhere.
 */
const FileTypeIcon: React.FC<FileTypeIconProps> = ({
  fileType,
  fallbackFilename,
  size = 36,
}) => {
  const info = getFileTypeInfo(fileType, fallbackFilename);

  // Aspect ratio is portrait (3:4) — looks like a document.
  const w = size;
  const h = Math.round(size * 1.15);

  // The folded corner is a triangle in the top-right; the page "sees"
  // a small notch where the fold lifts away.
  const FOLD = Math.round(size * 0.28);

  return (
    <svg
      width={w}
      height={h}
      viewBox={`0 0 ${w} ${h}`}
      role="img"
      aria-label={`${info.label} file`}
      style={{ display: 'block', flexShrink: 0 }}
    >
      <defs>
        {/* Subtle vertical sheen on the page so it doesn't read as a
            flat rectangle in dark-mode UI. */}
        <linearGradient id={`page-${info.ext}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#F8FAFC" />
          <stop offset="100%" stopColor="#E2E8F0" />
        </linearGradient>
      </defs>

      {/* Page body with the corner folded in (cuts off the top-right). */}
      <path
        d={`
          M 2 2
          L ${w - FOLD} 2
          L ${w - 2} ${FOLD}
          L ${w - 2} ${h - 2}
          L 2 ${h - 2}
          Z
        `}
        fill={`url(#page-${info.ext})`}
        stroke="rgba(15,23,42,0.18)"
        strokeWidth="1"
        strokeLinejoin="round"
      />

      {/* The folded triangle itself, slightly darker so it reads as a fold. */}
      <path
        d={`
          M ${w - FOLD} 2
          L ${w - FOLD} ${FOLD}
          L ${w - 2} ${FOLD}
          Z
        `}
        fill="#CBD5E1"
        stroke="rgba(15,23,42,0.18)"
        strokeWidth="1"
        strokeLinejoin="round"
      />

      {/* Coloured label band across the bottom — this is what tells the
          user what kind of file they're looking at. */}
      <rect
        x="2"
        y={h - Math.round(h * 0.42)}
        width={w - 4}
        height={Math.round(h * 0.28)}
        fill={info.color}
        rx="2"
      />

      {/* The 3-char extension label, centred in the band. Font size scales
          with icon size. */}
      <text
        x={w / 2}
        y={h - Math.round(h * 0.18)}
        textAnchor="middle"
        fontSize={Math.round(size * 0.3)}
        fontWeight={800}
        fill="#FFFFFF"
        fontFamily="-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
        letterSpacing="0.5"
      >
        {info.short}
      </text>
    </svg>
  );
};

export default FileTypeIcon;
